import i18nInst from "i18next";
import { isNil } from "lodash";

import {
  CURRENCY,
  NON_INTEGER_NUMBER_KEYS,
  PRICE_MAXIMUM_FRACTION_DIGITS,
} from "../constants";
import { SupportedLanguage } from "../models";

const languageToCurrency: Record<SupportedLanguage, keyof typeof CURRENCY> = {
  // Use system default currency (ILS) for both languages to avoid showing $ when UI is in English
  en: "ILS",
  he: "ILS",
} as const;

type SupportedCurrencies = (typeof languageToCurrency)[SupportedLanguage];

export const formatNumber = (value: number, decimalPlaces: number): string =>
  Math.round(parseFloat(`${value}e${decimalPlaces}`)) + `e-${decimalPlaces}`;

export const valueAsNumber = (value: string): number | "" => {
  if (value === "" || isNil(value)) {
    return "";
  }

  const cleaned = value.replace(/[^0-9.-]/g, "");

  const number = parseFloat(cleaned);

  return isNaN(number) ? "" : number;
};

/**
 * A native number input accepts these as partial text (`1.`, `1e`) while reporting an empty value,
 * so the typed text and the form value drift apart unless the keystroke is blocked outright.
 */
export const isNonIntegerNumberKey = (key: string): boolean => {
  return NON_INTEGER_NUMBER_KEYS.has(key);
};

interface IResolveOptionsArguments {
  isCurrency: boolean;
  maximumFractionDigits: number;
}

export type INumberFormatOptionsStyle = "percent" | "currency";

interface IResolveOptionsResult {
  maximumFractionDigits: number;
  style?: INumberFormatOptionsStyle;
  currency?: SupportedCurrencies;
}

const resolveOptions = ({
  isCurrency,
  maximumFractionDigits,
}: IResolveOptionsArguments): IResolveOptionsResult => {
  const result = { maximumFractionDigits } as IResolveOptionsResult;

  if (isCurrency && i18nInst.language in languageToCurrency) {
    const key = i18nInst.language as keyof typeof languageToCurrency;
    const currencyByLanguage = languageToCurrency[key];

    result.style = "currency";
    result.currency = currencyByLanguage;
  }

  return result;
};

interface IFormatPriceArguments {
  value: number;
  maximumFractionDigits?: number;
  isCurrency?: boolean;
}

export const formatPrice = ({
  value,
  maximumFractionDigits = PRICE_MAXIMUM_FRACTION_DIGITS,
  isCurrency = false,
}: IFormatPriceArguments): string => {
  const options = resolveOptions({ isCurrency, maximumFractionDigits });

  return new Intl.NumberFormat(i18nInst.language, options).format(value);
};

export function truncateDecimals(
  number: number | string,
  decimals = PRICE_MAXIMUM_FRACTION_DIGITS,
): number {
  if (isNil(number) || number === "") {
    return 0;
  }

  if (typeof number === "string") {
    number = parseFloat(number);
  }

  const divisor = Math.pow(10, decimals);
  return Math.round(number * divisor) / divisor;
}
